import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, Upload, TrendingUp, Users, Award, DollarSign } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListYourBusiness = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    email: '',
    phone: '',
    category: '',
    description: '',
    commissionRate: '',
    cookieDuration: '',
    products: '',
    founded: '',
    logo: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/business-listings/submit`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-12 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-bold text-stone-900 mb-4">Application Submitted!</h2>
          <p className="text-lg text-stone-600 mb-8">
            Thank you for your interest in joining our Garden Hub directory. Our team will review your application and get back to you within 2-3 business days.
          </p>
          <Button onClick={() => window.location.href = '/garden-hub'} className="bg-emerald-700 hover:bg-emerald-800">
            Return to Garden Hub
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-700 to-green-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">List Your Gardening Business</h1>
            <p className="text-xl text-emerald-100 mb-8">
              Join our premium directory and connect with thousands of gardening enthusiasts actively searching for quality products and services.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-4 gap-6 mt-16 max-w-5xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
              <div className="text-2xl font-bold mb-2">10K+</div>
              <div className="text-sm text-emerald-100">Monthly Visitors</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
              <div className="text-2xl font-bold mb-2">High</div>
              <div className="text-sm text-emerald-100">Conversion Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <Award className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
              <div className="text-2xl font-bold mb-2">Verified</div>
              <div className="text-sm text-emerald-100">Badge Included</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <DollarSign className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
              <div className="text-2xl font-bold mb-2">Free</div>
              <div className="text-sm text-emerald-100">To Start</div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">Application Form</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Info */}
              <div>
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Company Name *
                </label>
                <Input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="h-12"
                  placeholder="Your Company Name"
                  data-testid="company-name-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-stone-900 mb-2">
                    Website *
                  </label>
                  <Input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    required
                    className="h-12"
                    placeholder="https://yourcompany.com"
                    data-testid="website-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-900 mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12"
                    placeholder="contact@yourcompany.com"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-stone-900 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12"
                    placeholder="(555) 123-4567"
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-900 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="h-12 w-full px-4 border border-stone-300 rounded-lg"
                    data-testid="category-select"
                  >
                    <option value="">Select Category</option>
                    <option value="seeds">Seeds & Starts</option>
                    <option value="tools">Tools & Equipment</option>
                    <option value="plants">Plants & Trees</option>
                    <option value="indoor">Indoor & Hydroponic</option>
                    <option value="succulents">Succulents & Gifts</option>
                    <option value="decor">Garden Decor</option>
                    <option value="systems">Growing Systems</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Company Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg"
                  placeholder="Tell us about your company, products, and what makes you unique..."
                  data-testid="description-textarea"
                />
              </div>

              {/* Affiliate Info */}
              <div className="border-t border-stone-200 pt-6">
                <h3 className="text-lg font-bold text-stone-900 mb-4">Affiliate Program Details</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-2">
                      Commission Rate *
                    </label>
                    <Input
                      type="text"
                      name="commissionRate"
                      value={formData.commissionRate}
                      onChange={handleChange}
                      required
                      className="h-12"
                      placeholder="e.g., 10%, 8-15%"
                      data-testid="commission-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-2">
                      Cookie Duration *
                    </label>
                    <Input
                      type="text"
                      name="cookieDuration"
                      value={formData.cookieDuration}
                      onChange={handleChange}
                      required
                      className="h-12"
                      placeholder="e.g., 30 days, 60 days"
                      data-testid="cookie-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-2">
                      Number of Products
                    </label>
                    <Input
                      type="text"
                      name="products"
                      value={formData.products}
                      onChange={handleChange}
                      className="h-12"
                      placeholder="e.g., 500+, 1000+"
                      data-testid="products-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-900 mb-2">
                      Year Founded
                    </label>
                    <Input
                      type="text"
                      name="founded"
                      value={formData.founded}
                      onChange={handleChange}
                      className="h-12"
                      placeholder="e.g., 2015"
                      data-testid="founded-input"
                    />
                  </div>
                </div>
              </div>

              {/* Logo Upload */}
              <div className="border-t border-stone-200 pt-6">
                <label className="block text-sm font-semibold text-stone-900 mb-2">
                  Company Logo
                </label>
                <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors">
                  <Upload className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                  <p className="text-sm text-stone-600 mb-2">Drop your logo here or click to upload</p>
                  <p className="text-xs text-stone-500">PNG, JPG up to 5MB (recommended: 400x400px)</p>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setFormData({...formData, logo: e.target.files[0]})}
                    data-testid="logo-upload"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="border-t border-stone-200 pt-6">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-lg"
                  data-testid="submit-application-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
                <p className="text-sm text-stone-500 text-center mt-4">
                  By submitting, you agree to our terms and conditions. We'll review your application within 2-3 business days.
                </p>
              </div>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ListYourBusiness;
